const API_BASE = "/api";

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {};
  
  // Only set Content-Type for requests with body
  if (options?.body) {
    headers["Content-Type"] = "application/json";
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || error.message || "Request failed");
  }

  return response.json();
}

// Products
export const products = {
  list: () => request<Product[]>("/products"),
  get: (id: string) => request<Product>(`/products/${id}`),
  create: (data: CreateProductInput) => request<Product>("/products", { method: "POST", body: JSON.stringify(data) }),
  preview: (data: { retailer: string; url?: string; productId?: string }) => 
    request<ProductPreview>("/products/preview", { method: "POST", body: JSON.stringify(data) }),
  fetch: (url: string) => 
    request<FetchedProduct>("/products/fetch", { method: "POST", body: JSON.stringify({ url }) }),
  update: (id: string, data: { name?: string; sku?: string; price?: number; status?: string }) => 
    request<Product>(`/products/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/products/${id}`, { method: "DELETE" }),
};

// Profiles
export const profiles = {
  list: () => request<Profile[]>("/profiles"),
  get: (id: string) => request<Profile>(`/profiles/${id}`),
  create: (data: CreateProfileInput) => request<Profile>("/profiles", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Partial<CreateProfileInput>) => request<Profile>(`/profiles/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/profiles/${id}`, { method: "DELETE" }),
  addresses: {
    list: () => request<Address[]>("/profiles/addresses"),
    create: (data: CreateAddressInput) => request<Address>("/profiles/addresses", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateAddressInput>) => request<Address>(`/profiles/addresses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) => request<void>(`/profiles/addresses/${id}`, { method: "DELETE" }),
  },
  paymentMethods: {
    list: () => request<PaymentMethod[]>("/profiles/payment-methods"),
    create: (data: CreatePaymentMethodInput) => request<PaymentMethod>("/profiles/payment-methods", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreatePaymentMethodInput>) => request<PaymentMethod>(`/profiles/payment-methods/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) => request<void>(`/profiles/payment-methods/${id}`, { method: "DELETE" }),
  },
};

// Tasks
export const tasks = {
  list: () => request<Task[]>("/tasks"),
  get: (id: string) => request<Task>(`/tasks/${id}`),
  create: (data: CreateTaskInput) => request<Task>("/tasks", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Partial<CreateTaskInput>) => request<Task>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/tasks/${id}`, { method: "DELETE" }),
  start: (id: string) => request<{ success: boolean }>(`/tasks/${id}/start`, { method: "POST" }),
  stop: (id: string) => request<{ success: boolean }>(`/tasks/${id}/stop`, { method: "POST" }),
  startAll: () => request<{ started: number }>("/tasks/start-all", { method: "POST" }),
  stopAll: () => request<{ stopped: number }>("/tasks/stop-all", { method: "POST" }),
};

// Proxies
export const proxies = {
  list: () => request<{ proxies: Proxy[]; stats: ProxyStats }>("/proxies"),
  create: (data: CreateProxyInput) => request<Proxy>("/proxies", { method: "POST", body: JSON.stringify(data) }),
  bulkImport: (proxies: string) => request<{ imported: number }>("/proxies/bulk", { method: "POST", body: JSON.stringify({ proxies }) }),
  delete: (id: string) => request<void>(`/proxies/${id}`, { method: "DELETE" }),
  test: (id: string) => request<{ success: boolean; ip?: string; latency?: number }>(`/proxies/${id}/test`, { method: "POST" }),
  clear: () => request<void>("/proxies", { method: "DELETE" }),
};

// Orders
export const orders = {
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const search = new URLSearchParams();
    if (params?.page) search.set("page", params.page.toString());
    if (params?.limit) search.set("limit", params.limit.toString());
    if (params?.status) search.set("status", params.status);
    return request<{ orders: Order[]; pagination: Pagination }>(`/orders?${search}`);
  },
  stats: (period?: string) => request<OrderStats>(`/orders/stats${period ? `?period=${period}` : ""}`),
  delete: (id: string) => request<void>(`/orders/${id}`, { method: "DELETE" }),
};

// Settings
export const settings = {
  get: () => request<Record<string, unknown>>("/settings"),
  set: (key: string, value: unknown) => request<void>(`/settings/${key}`, { method: "PUT", body: JSON.stringify({ value }) }),
  system: () => request<SystemInfo>("/settings/system"),
  testDiscord: (webhookUrl?: string) => request<{ success: boolean }>("/settings/notifications/test", { method: "POST", body: JSON.stringify({ webhookUrl }) }),
};

// Logs
export const logs = {
  list: (params?: { level?: string; source?: string; search?: string; taskId?: string; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.level) search.set("level", params.level);
    if (params?.source) search.set("source", params.source);
    if (params?.search) search.set("search", params.search);
    if (params?.taskId) search.set("taskId", params.taskId);
    if (params?.limit) search.set("limit", params.limit.toString());
    return request<{ logs: LogEntry[]; total: number }>(`/logs?${search}`);
  },
  stats: () => request<LogStats>("/logs/stats"),
  clear: () => request<{ success: boolean }>("/logs", { method: "DELETE" }),
  clearOld: (hours: number) => request<{ success: boolean; deleted: number }>(`/logs/old?hours=${hours}`, { method: "DELETE" }),
};

// Keyword Search
export const keywordSearch = {
  list: () => request<KeywordSearch[]>("/search"),
  get: (id: string) => request<KeywordSearch>(`/search/${id}`),
  create: (data: CreateKeywordSearch) => request<KeywordSearch>("/search", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Partial<CreateKeywordSearch> & { enabled?: boolean }) => 
    request<KeywordSearch>(`/search/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id: string) => request<{ success: boolean }>(`/search/${id}`, { method: "DELETE" }),
  start: (id: string) => request<{ success: boolean }>(`/search/${id}/start`, { method: "POST" }),
  stop: (id: string) => request<{ success: boolean }>(`/search/${id}/stop`, { method: "POST" }),
  run: (id: string) => request<KeywordSearch>(`/search/${id}/run`, { method: "POST" }),
  getResults: (id: string) => request<KeywordSearchResult[]>(`/search/${id}/results`),
  clearResults: (id: string) => request<{ success: boolean }>(`/search/${id}/results`, { method: "DELETE" }),
  getActive: () => request<{ activeSearchIds: string[]; count: number }>("/search/status/active"),
};

// Queue Manager
export const queue = {
  list: () => request<{ active: QueueSession[]; history: QueueSession[] }>("/queue"),
  getActive: () => request<QueueSession[]>("/queue/active"),
  getStats: () => request<QueueStats>("/queue/stats"),
  create: (data: CreateQueueSession) => request<QueueSession>("/queue", { method: "POST", body: JSON.stringify(data) }),
  get: (id: string) => request<QueueSession>(`/queue/${id}`),
  update: (id: string, data: Partial<QueueSession>) => 
    request<QueueSession>(`/queue/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  markReady: (id: string) => request<QueueSession>(`/queue/${id}/ready`, { method: "POST" }),
  markComplete: (id: string) => request<QueueSession>(`/queue/${id}/complete`, { method: "POST" }),
  delete: (id: string) => request<{ success: boolean }>(`/queue/${id}`, { method: "DELETE" }),
  clearOld: () => request<{ clearedMemory: number; clearedDb: number }>("/queue/old", { method: "DELETE" }),
  getHistory: (id: string) => request<QueueUpdate[]>(`/queue/${id}/history`),
};

// Types
export interface Product {
  id: string;
  retailer: string;
  productId: string;
  url: string;
  name: string;
  description?: string;
  sku?: string;
  price?: number;
  imageUrl?: string;
  status: string;
  lastChecked?: string;
}

export interface Profile {
  id: string;
  name: string;
  email: string;
  shippingAddress: Address;
  paymentMethod: PaymentMethod;
  isDefault: boolean;
}

export interface Address {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  isDefault: boolean;
}

export interface PaymentMethod {
  id: string;
  name: string;
  type: string;
  cardNumber?: string;
  isDefault: boolean;
}

export interface Task {
  id: string;
  product: Product;
  profile: Profile;
  quantity: number;
  maxPrice?: number;
  checkoutMethod: string;
  retryCount: number;
  monitorDelay: number;
  enabled: boolean;
  status: string;
  successCount: number;
  failCount: number;
  maxSuccessCount?: number;
  maxTotalSpend?: number;
  totalSpent: number;
  maxQueueWaitMinutes: number;
  queuePosition?: number;
  queueEstimatedWait?: number;
}

export interface Proxy {
  id: string;
  host: string;
  port: number;
  protocol: string;
  isHealthy: boolean;
  failCount: number;
}

export interface ProxyStats {
  total: number;
  healthy: number;
  unhealthy: number;
}

export interface Order {
  id: string;
  retailer: string;
  orderId?: string;
  status: string;
  totalPrice?: number;
  quantity: number;
  checkoutMethod: string;
  duration: number;
  errorMessage?: string;
  createdAt: string;
  product: Product;
  profile: Profile;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface OrderStats {
  totalOrders: number;
  successfulOrders: number;
  failedOrders: number;
  successRate: number;
  totalSpent: number;
  avgCheckoutTime: number;
}

export interface SystemInfo {
  products: number;
  profiles: number;
  tasks: { total: number; active: number };
  proxies: number;
  orders: number;
  uptime: number;
  memory: { heapUsed: number; heapTotal: number };
  nodeVersion: string;
}

export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogSource = "api" | "monitor" | "checkout" | "proxy" | "queue" | "captcha" | "notification" | "system";

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  source: LogSource;
  message: string;
  data?: Record<string, unknown>;
  taskId?: string;
  productId?: string;
  error?: string;
  stack?: string;
}

export interface LogStats {
  total: number;
  byLevel: Record<LogLevel, number>;
  bySource: Record<LogSource, number>;
  errorsLast24h: number;
  warningsLast24h: number;
}

// Keyword Search Types
export interface KeywordSearch {
  id: string;
  name: string;
  retailer: string;
  keywords: string;
  excludeKeywords?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  enabled: boolean;
  autoCreateTask: boolean;
  profileId?: string;
  profile?: { id: string; name: string };
  notifyDiscord: boolean;
  lastChecked?: string;
  checkInterval: number;
  status: "idle" | "searching" | "found" | "error";
  lastFoundCount: number;
  createdAt: string;
  updatedAt: string;
  foundProducts?: KeywordSearchResult[];
}

export interface KeywordSearchResult {
  id: string;
  keywordSearchId: string;
  retailer: string;
  productId: string;
  url: string;
  name: string;
  price?: number;
  imageUrl?: string;
  inStock: boolean;
  firstFoundAt: string;
  lastSeenAt: string;
  notified: boolean;
  taskCreated: boolean;
}

export interface CreateKeywordSearch {
  name: string;
  retailer: "walmart" | "pokemoncenter";
  keywords: string;
  excludeKeywords?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  autoCreateTask?: boolean;
  profileId?: string;
  notifyDiscord?: boolean;
  checkInterval?: number;
}

// Queue Types
export interface QueueSession {
  id: string;
  taskId?: string;
  retailer: string;
  targetUrl: string;
  queueId?: string;
  eventId?: string;
  ticket?: string;
  position?: number;
  estimatedWait?: number;
  usersAhead?: number;
  status: "waiting" | "ready" | "expired" | "completed" | "error";
  startedAt: string;
  readyAt?: string;
  expiresAt?: string;
  completedAt?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  task?: {
    id: string;
    product: { name: string; retailer: string };
  };
}

export interface QueueUpdate {
  id: string;
  sessionId: string;
  position?: number;
  estimatedWait?: number;
  usersAhead?: number;
  status: string;
  timestamp: string;
}

export interface QueueStats {
  total: number;
  waiting: number;
  ready: number;
  completed: number;
  expired: number;
  errors: number;
  byRetailer: Record<string, number>;
  averageWaitTime: number | null;
}

export interface CreateQueueSession {
  taskId?: string;
  retailer?: string;
  targetUrl: string;
  queueId?: string;
  eventId?: string;
}

// Input types
export interface CreateProductInput {
  url?: string;
  productId?: string;
  retailer: string;
  name?: string;
  description?: string;
  price?: number;
  imageUrl?: string;
  status?: string;
  sku?: string;
}

export interface ProductPreview {
  valid: boolean;
  retailer: string;
  productId: string;
  url: string;
  alreadyExists: boolean;
  existingProduct?: Product;
}

export interface FetchedProduct {
  retailer: string;
  productId: string;
  url: string;
  name: string;
  description?: string;
  price?: number;
  imageUrl?: string;
  status: string;
  sku?: string;
  brand?: string;
  alreadyExists: boolean;
  existingProduct?: Product;
}

export interface CreateProfileInput {
  name: string;
  email: string;
  shippingAddressId: string;
  paymentMethodId: string;
}

export interface CreateAddressInput {
  name: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  phone: string;
}

export interface CreatePaymentMethodInput {
  name: string;
  type: string;
  cardNumber?: string;
  cardExpMonth?: string;
  cardExpYear?: string;
  cardCvv?: string;
  cardholderName?: string;
}

export interface CreateTaskInput {
  productId: string;
  profileId: string;
  quantity?: number;
  maxPrice?: number;
  checkoutMethod?: string;
  retryCount?: number;
  monitorDelay?: number;
  maxSuccessCount?: number;
  maxTotalSpend?: number;
  maxQueueWaitMinutes?: number;
}

export interface CreateProxyInput {
  host: string;
  port: number;
  username?: string;
  password?: string;
  protocol?: string;
}

